import { useState } from "react";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorState } from "../../components/ui/ErrorState";
import { Button } from "../../components/ui/Button";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { ErrorText } from "../../components/ui/feedback";
import { LabelGrid } from "../../components/ui/LabelGrid";
import { PageHeader } from "../../components/ui/PageHeader";
import { useI18n } from "../../i18n/I18nProvider";
import { messageKeyForError } from "../../i18n/errors";
import type { Person } from "../../lib/api-types";
import { usePersonMutations } from "./mutations";
import { InvitePlayerModal } from "./InvitePlayerModal";
import { LinkModal } from "./LinkModal";
import { PersonModal } from "./PersonModal";
import { RosterRow } from "./RosterRow";
import { RosterSkeleton } from "./RosterSkeleton";
import { useChangedPersonIds, usePersons } from "./queries";
import { StatusFilter, type StatusFilterValue } from "./StatusFilter";

export interface RosterPageProps {
  workspaceId: string;
}

type Dialog =
  | { kind: "create" }
  | { kind: "invite" }
  | { kind: "edit"; person: Person }
  | { kind: "delete"; person: Person }
  | { kind: "regenerate"; person: Person }
  | null;

interface ShownLink {
  personName: string;
  personNumber: number | null;
  url: string;
  regenerated: boolean;
}

/** Roster: label-grid rows of people, a status filter, and one action plus an overflow menu per row. */
export function RosterPage({ workspaceId }: RosterPageProps) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<StatusFilterValue>("active");
  const [dialog, setDialog] = useState<Dialog>(null);
  const [shownLink, setShownLink] = useState<ShownLink | null>(null);
  const persons = usePersons(workspaceId, filter === "deleted");
  const changed = useChangedPersonIds(workspaceId);
  const mutations = usePersonMutations(workspaceId);

  const closeDialog = () => setDialog(null);
  const busy = mutations.create.isPending || mutations.update.isPending;

  const showLink = (name: string, number: number | null, url: string, regenerated = false) =>
    setShownLink({ personName: name, personNumber: number, url, regenerated });

  const createLink = (person: Person) => {
    mutations.regenerateLink.mutate(person.id, {
      onSuccess: (result) => showLink(person.name, person.number, result.url),
    });
  };

  const confirmDialog = (): void => {
    if (dialog?.kind === "delete") {
      mutations.remove.mutate(dialog.person.id, { onSuccess: closeDialog });
    } else if (dialog?.kind === "regenerate") {
      const { person } = dialog;
      mutations.regenerateLink.mutate(person.id, {
        onSuccess: (result) => {
          setDialog(null);
          showLink(person.name, person.number, result.url, true);
        },
      });
    }
  };

  const openCreate = () => setDialog({ kind: "create" });
  const openInvite = () => setDialog({ kind: "invite" });
  const mutationError =
    mutations.remove.error ??
    mutations.restore.error ??
    mutations.update.error ??
    mutations.regenerateLink.error;

  const renderBody = () => {
    if (persons.isPending) return <RosterSkeleton />;
    if (persons.isError) {
      return (
        <ErrorState
          action={<Button onClick={() => void persons.refetch()}>{t("roster.tryAgain")}</Button>}
        >
          {t(messageKeyForError(persons.error))}
        </ErrorState>
      );
    }
    const rows = persons.data ?? [];
    if (rows.length === 0) {
      if (filter === "deleted") return <EmptyState>{t("roster.emptyDeleted")}</EmptyState>;
      return (
        <EmptyState
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="primary" onClick={openInvite}>
                {t("roster.invite")}
              </Button>
              <Button onClick={openCreate}>{t("roster.add")}</Button>
            </div>
          }
        >
          {t("roster.empty")}
        </EmptyState>
      );
    }
    // When every listed person changed, a flag on each row carries no information: hide them.
    const everyoneChanged = filter === "active" && rows.every((person) => changed.has(person.id));
    return (
      <LabelGrid>
        {rows.map((person) => (
          <RosterRow
            key={person.id}
            workspaceId={workspaceId}
            person={person}
            changed={!everyoneChanged && changed.has(person.id)}
            lockBusy={mutations.update.isPending}
            onEdit={() => setDialog({ kind: "edit", person })}
            onCreateLink={() => createLink(person)}
            onShowLink={(url) => showLink(person.name, person.number, url)}
            onNewLink={() => setDialog({ kind: "regenerate", person })}
            onToggleLock={() =>
              mutations.update.mutate({
                personId: person.id,
                patch: { photo_locked: !person.photoLocked },
              })
            }
            onToggleSongLock={() =>
              mutations.update.mutate({
                personId: person.id,
                patch: { song_locked: !person.songLocked },
              })
            }
            onDelete={() => setDialog({ kind: "delete", person })}
            onRestore={() => mutations.restore.mutate(person.id)}
          />
        ))}
      </LabelGrid>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("roster.title")}
        actions={
          <>
            <StatusFilter value={filter} onChange={setFilter} />
            <Button onClick={openCreate}>{t("roster.add")}</Button>
            <Button variant="primary" onClick={openInvite}>
              {t("roster.invite")}
            </Button>
          </>
        }
      />
      <ErrorText error={mutationError} />
      {renderBody()}

      {dialog?.kind === "create" || dialog?.kind === "edit" ? (
        <PersonModal
          person={dialog.kind === "edit" ? dialog.person : undefined}
          busy={busy}
          error={mutations.create.error ?? mutations.update.error}
          onClose={closeDialog}
          onSubmit={(input) => {
            if (dialog.kind === "edit") {
              mutations.update.mutate(
                { personId: dialog.person.id, patch: input },
                { onSuccess: closeDialog },
              );
            } else {
              mutations.create.mutate(input, { onSuccess: closeDialog });
            }
          }}
        />
      ) : null}

      {dialog?.kind === "invite" ? (
        <InvitePlayerModal
          busy={mutations.invite.isPending}
          error={mutations.invite.error}
          onClose={closeDialog}
          onSubmit={(input) =>
            mutations.invite.mutate(input, {
              onSuccess: (result) => {
                setDialog(null);
                showLink(result.person.name, result.person.number, result.url);
              },
            })
          }
        />
      ) : null}

      {dialog?.kind === "delete" ? (
        <ConfirmDialog
          title={t("common.delete")}
          message={t("roster.confirmDelete", { name: dialog.person.name })}
          confirmLabel={t("common.delete")}
          variant="danger"
          busy={mutations.remove.isPending}
          onConfirm={confirmDialog}
          onCancel={closeDialog}
        />
      ) : null}

      {dialog?.kind === "regenerate" ? (
        <ConfirmDialog
          title={t("roster.newLink")}
          message={t("roster.confirmRegenerate", { name: dialog.person.name })}
          confirmLabel={t("roster.newLink")}
          busy={mutations.regenerateLink.isPending}
          error={mutations.regenerateLink.error}
          onConfirm={confirmDialog}
          onCancel={closeDialog}
        />
      ) : null}

      {shownLink ? (
        <LinkModal
          personName={shownLink.personName}
          personNumber={shownLink.personNumber}
          url={shownLink.url}
          regenerated={shownLink.regenerated}
          onClose={() => setShownLink(null)}
        />
      ) : null}
    </div>
  );
}
